import {
  OrganizationGetMeQuery,
  UserGetMeQuery,
  UserUpdateHostDataMutation,
  UserUpdateHostDataMutationVariables
} from '@/API';
import config from '@/config';
import { userUpdateHostData } from '@/graphql/mutations';
import { organizationGetMe, userGetMe } from '@/graphql/queries';
import { generateClient, GraphQLQuery } from 'aws-amplify/api';
import * as Auth from 'aws-amplify/auth';
import {
  AuthUser,
  ConfirmResetPasswordInput,
  ConfirmSignInInput,
  ConfirmSignUpInput,
  ResendSignUpCodeInput,
  ResetPasswordInput,
  SignInInput,
  SignUpInput,
  UpdateUserAttributesInput,
  UpdateUserAttributesOutput
} from 'aws-amplify/auth';
import { createContext, useContext, useEffect, useState } from 'react';

const client = generateClient();

interface HostData {
  hostTipsEnabled: boolean;
  hasHostedGame: boolean;
}

interface OrganizationData {
  id: string;
  name: string;
  onFreeTrial: boolean;
  freeTrialExpiration?: string | null;
  organizationActive: boolean;
  freeTrialDaysRemaining: number | null;
}

interface AuthState {
  loaded: boolean;
  user: AuthUser | undefined;
  userAttributes: Auth.FetchUserAttributesOutput | undefined;
  hostData: HostData | undefined;
  organization: OrganizationData | undefined;
  isTrialExpired: boolean;
  groups: string[];
  tenant: string | undefined;
  token: () => Promise<any>;

  signUp(input: SignUpInput): Promise<Auth.SignUpOutput | undefined>;
  resendSignUpCode(input: ResendSignUpCodeInput): Promise<boolean>;
  confirmSignUp(input: ConfirmSignUpInput): Promise<Auth.ConfirmSignUpOutput | undefined>;
  signIn(input: SignInInput): Promise<Auth.SignInOutput | undefined>;
  confirmSignIn(input: ConfirmSignInInput): Promise<Auth.ConfirmSignInOutput | undefined>;
  signOut(): Promise<void>;
  forceRefresh: () => Promise<void>;
  resetPassword(input: Auth.ResetPasswordInput): Promise<void>;
  confirmResetPassword(input: Auth.ConfirmResetPasswordInput): Promise<void>;
  updateUserAttributes(input: UpdateUserAttributesInput): Promise<UpdateUserAttributesOutput>;
  updateHostData(hostData: Partial<HostData>): Promise<void>;
}

export const AuthContext = createContext<AuthState>({} as AuthState);

export const useAuth = () => useContext(AuthContext);

// Helper function to calculate days remaining in free trial
const calculateDaysRemaining = (freeTrialExpiration: string | null | undefined): number | null => {
  if (!freeTrialExpiration) return null;

  // Create date in local timezone at end of day
  const expirationDate = new Date(freeTrialExpiration);
  const now = new Date();

  // Calculate difference in days
  const diffTime = expirationDate.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return Math.max(0, diffDays);
};

interface AuthProps {
  children: React.ReactNode;
}

export const AuthProvider = ({ children }: AuthProps) => {
  const [loaded, setLoaded] = useState(false);
  const [user, setUser] = useState<Auth.AuthUser | undefined>(undefined);
  const [groups, setGroups] = useState<string[]>([]);
  const [hostData, setHostData] = useState<HostData | undefined>(undefined);
  const [organization, setOrganization] = useState<OrganizationData | undefined>(undefined);
  const [isTrialExpired, setIsTrialExpired] = useState(false);
  const [userAttributes, setUserAttributes] = useState<Auth.FetchUserAttributesOutput | undefined>(
    undefined
  );
  const siteDomain = config.siteDomain;

  // Function to check tenant organization and navigate if needed
  const checkTenantNavigation = (subdomain: string | undefined) => {
    // Do nothing if current site is localhost
    if (window.location.hostname === 'localhost' || !subdomain) {
      return;
    }

    // Extract subdomain from current URL
    const currentUrl = window.location.hostname;
    const currentSubdomain = currentUrl.split('.')[0];

    // Check if tenant org is different from subdomain
    if (subdomain !== currentSubdomain) {
      let newUrl;
      if (window.location.pathname.includes('auth')) {
        newUrl = `${window.location.protocol}//${subdomain}.${siteDomain}`;
      } else {
        newUrl = `${window.location.protocol}//${subdomain}.${siteDomain}${window.location.pathname}${window.location.search}${window.location.hash}`;
      }
      window.location.href = newUrl;
    }
  };

  useEffect(() => {
    checkUser();

    // Set up global error handler for unhandled promise rejections (GraphQL errors)
    const handleUnhandledRejection = async (event: PromiseRejectionEvent) => {
      const error = event.reason;

      // Check if it's a TRIAL_EXPIRED error
      const isTrialExpiredError = error?.errors?.some(
        (err: any) => err?.extensions?.code === 'TRIAL_EXPIRED'
      );

      if (isTrialExpiredError) {
        event.preventDefault(); // Prevent default error handling
        setIsTrialExpired(true);

        await Auth.signOut();

        clearUser();

        // Force navigation to sign-in page

        window.location.href = '/auth/sign-in';
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  async function token() {
    // Must be the encoded JWT string: Apollo places this directly into the
    // Authorization header and the API verifies it with aws-jwt-verify. Returning
    // `accessToken.payload` (an object) serialized to "[object Object]", which made
    // every Apollo request anonymous.
    return (await Auth.fetchAuthSession()).tokens?.accessToken?.toString();
  }

  async function signUp(input: SignUpInput) {
    const result = await Auth.signUp(input);
    await checkUser();
    return result;
  }

  async function resendSignUpCode(input: ResendSignUpCodeInput) {
    await Auth.resendSignUpCode(input);
    return true;
  }

  async function confirmSignUp(input: ConfirmSignUpInput) {
    return await Auth.confirmSignUp(input);
  }

  async function confirmSignIn(input: ConfirmSignInInput) {
    const result = await Auth.confirmSignIn(input);
    await checkUser();
    return result;
  }

  async function signIn(input: SignInInput) {
    const result = await Auth.signIn(input);
    await checkUser();
    return result;
  }

  async function updateUserAttributes(input: UpdateUserAttributesInput) {
    const result = await Auth.updateUserAttributes(input);
    await checkUser();
    return result;
  }

  async function updateHostData(hostData: { hostTipsEnabled?: boolean; hasHostedGame?: boolean }) {
    await client.graphql<GraphQLQuery<UserUpdateHostDataMutation>>({
      query: userUpdateHostData,
      variables: {
        input: {
          ...hostData
        }
      } as UserUpdateHostDataMutationVariables
    });

    // Build the update object with only defined values
    const updates: Partial<HostData> = {};
    if (hostData.hostTipsEnabled !== undefined) {
      updates.hostTipsEnabled = hostData.hostTipsEnabled;
    }
    if (hostData.hasHostedGame !== undefined) {
      updates.hasHostedGame = hostData.hasHostedGame;
    }

    setHostData((prevHostData) => {
      if (!prevHostData) return prevHostData;
      return {
        ...prevHostData,
        ...updates
      };
    });
  }

  async function signOut() {
    await Auth.signOut();
    clearUser();
    setIsTrialExpired(false);
  }

  async function checkUser() {
    try {
      const user = await Auth.getCurrentUser();

      if (user) {
        const userAttributes = await Auth.fetchUserAttributes();
        const groups =
          ((await Auth.fetchAuthSession()).tokens?.accessToken.payload[
            'cognito:groups'
          ] as string[]) ?? [];

        // Fetch organization data to check trial status BEFORE setting user
        const orgData = await client.graphql<GraphQLQuery<OrganizationGetMeQuery>>({
          query: organizationGetMe
        });

        if (orgData.data.organizationGetMe) {
          const org = orgData.data.organizationGetMe;

          // Check if trial is expired (expires at END of the expiration date)
          let trialExpired = false;
          let daysRemaining: number | null = null;

          if (org.onFreeTrial && org.freeTrialExpiration) {
            daysRemaining = calculateDaysRemaining(org.freeTrialExpiration);
            trialExpired = daysRemaining === 0;
          }

          // If trial is expired, sign out immediately and show modal
          if (trialExpired) {
            setIsTrialExpired(true);
            await Auth.signOut();
            clearUser();
            setLoaded(true);
            return;
          }

          checkTenantNavigation(org.subdomain);

          setOrganization({
            id: org.id,
            name: org.name,
            onFreeTrial: org.onFreeTrial,
            freeTrialExpiration: org.freeTrialExpiration,
            freeTrialDaysRemaining: daysRemaining,
            organizationActive: org.organizationActive ?? true
          });
          setIsTrialExpired(false);
        }

        setUser(user);
        setUserAttributes(userAttributes);
        const dbUser = await client.graphql<GraphQLQuery<UserGetMeQuery>>({ query: userGetMe });
        if (dbUser.data.userGetMe) {
          setHostData({
            hostTipsEnabled: dbUser.data.userGetMe.hostTipsEnabled,
            hasHostedGame: dbUser.data.userGetMe.hasHostedGame
          });
        }

        setGroups(groups);
      } else {
        await Auth.signOut();
        clearUser();
      }
    } catch {
      await Auth.signOut();
      clearUser();
    } finally {
      setLoaded(true);
    }
  }

  async function clearUser() {
    setUser(undefined);
    setUserAttributes(undefined);
    setOrganization(undefined);
  }

  async function forceRefresh() {
    await Auth.fetchAuthSession({ forceRefresh: true });
    await checkUser();
  }

  async function resetPassword(input: ResetPasswordInput) {
    await Auth.resetPassword(input);
  }

  async function confirmResetPassword(input: ConfirmResetPasswordInput) {
    await Auth.confirmResetPassword(input);
  }

  return (
    <AuthContext.Provider
      value={{
        loaded,
        user,
        userAttributes,
        hostData,
        organization,
        isTrialExpired,
        groups,
        tenant: groups.find((group) => group.startsWith('tenant:'))?.split(':')[1],
        token,

        signUp,
        resendSignUpCode,
        confirmSignUp,
        signIn,
        confirmSignIn,
        signOut,
        forceRefresh,
        resetPassword,
        confirmResetPassword,
        updateUserAttributes,
        updateHostData
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
